<script setup lang="ts">
import { VForm } from 'vuetify/components';
import { ref } from 'vue';
import { useValidationErrorStore } from '@/stores/validationErrorStore';
import { generateInputId } from '@/utils/idUtils';

type PropsType = {
    disabled?: boolean;
    readonly?: boolean;
    validateOn?: VForm['$props']['validateOn'];
    formId?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    disabled: false,
    readonly: false,
    formId: () => generateInputId('form'),
});

const emit = defineEmits<{
    submit: [event: Event];
}>();

defineOptions({ inheritAttrs: false });

const formRef = ref<InstanceType<typeof VForm>>();

const validationStore = useValidationErrorStore();
validationStore.setCurrentForm(props.formId);

const validate = async (): Promise<{ valid: boolean; errors: any[] }> => {
    if (!formRef.value) {
        return { valid: false, errors: [] };
    }

    const result = await formRef.value.validate();

    return result || { valid: false, errors: [] };
};

const reset = () => {
    formRef.value?.reset();
};

const resetValidation = () => {
    formRef.value?.resetValidation();
};

const handleSubmit = (event: Event) => {
    emit('submit', event);
};

defineExpose({
    validate,
    reset,
    resetValidation,
});
</script>

<template>
    <VForm
        ref="formRef"
        :disabled="disabled"
        :readonly="readonly"
        :validate-on="validateOn"
        v-bind="$attrs"
        @submit="handleSubmit"
    >
        <slot />
    </VForm>
</template>
